import { BrokerDetail } from "../../../types/brokers";

const fmtDate = (iso: string | null): string => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

export default function BrokerOverviewTab({
  broker,
}: {
  broker: BrokerDetail;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
        gap: "1rem",
      }}
    >
      <Section title="Identificación">
        <Field label="Nombre" value={broker.brokerName} />
        <Field label="Razón social" value={broker.businessName} />
        <Field label="CIF / NIF" value={broker.brokerIdCard} mono />
        <Field label="DGSFP" value={broker.brokerIdDgsfp} mono />
        <Field label="Slug" value={broker.slug} mono />
        <Field label="External ID" value={broker.externalId} mono />
        <Field
          label="Provider ID"
          value={
            broker.brokerProviderId !== null ? String(broker.brokerProviderId) : null
          }
          mono
        />
      </Section>

      <Section title="Contacto">
        <Field label="Email" value={broker.brokerEmail} link={`mailto:${broker.brokerEmail ?? ""}`} />
        <Field label="Teléfono" value={broker.brokerPhone} link={`tel:${broker.brokerPhone ?? ""}`} />
        <Field label="Dirección" value={broker.address} />
        <Field label="URL complementaria" value={broker.complementaryUrl} link={broker.complementaryUrl ?? undefined} />
      </Section>

      <Section title="Configuración comercial">
        <Field label="MEDOR" value={broker.medorCode} mono />
        <Field
          label="Mercados"
          value={broker.markets.length === 0 ? null : broker.markets.join(", ")}
          mono
        />
        <Field
          label="Color tema"
          value={broker.themeColor}
          swatchColor={broker.themeColor ?? undefined}
        />
        <Field label="Logo" value={broker.logoFilename} mono />
      </Section>

      <Section title="Estado y compliance">
        <Field
          label="Cuestionario completado"
          value={fmtDate(broker.questionnaireCompletedAt)}
        />
        <Field label="Términos aceptados" value={fmtDate(broker.termsAcceptedAt)} />
        <Field label="RC aceptado" value={fmtDate(broker.rcAcceptedAt)} />
        <Field label="DGS auth aceptado" value={fmtDate(broker.dgsAuthAcceptedAt)} />
      </Section>

      {broker.htmlCode && (
        <Section title="HTML embebido" full>
          <pre
            style={{
              background: "#1a1a2e",
              color: "#dcdce4",
              padding: "0.75rem",
              borderRadius: "0.45rem",
              fontSize: ".72rem",
              overflow: "auto",
              maxHeight: 220,
              margin: 0,
            }}
          >
            {broker.htmlCode}
          </pre>
        </Section>
      )}
    </div>
  );
}

function Section({
  title,
  full,
  children,
}: {
  title: string;
  full?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        gridColumn: full ? "1 / -1" : undefined,
        display: "flex",
        flexDirection: "column",
        gap: "0.4rem",
      }}
    >
      <h3
        style={{
          margin: 0,
          fontSize: ".75rem",
          textTransform: "uppercase",
          letterSpacing: ".3px",
          color: "#73879C",
          fontWeight: 700,
          borderBottom: "1px solid #e9ecef",
          paddingBottom: "0.3rem",
        }}
      >
        {title}
      </h3>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
        {children}
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  mono,
  link,
  swatchColor,
}: {
  label: string;
  value: string | null;
  mono?: boolean;
  link?: string;
  swatchColor?: string;
}) {
  const rendered = value && value !== "—" ? value : null;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "0.4fr 0.6fr",
        gap: "0.5rem",
        fontSize: ".82rem",
        alignItems: "center",
      }}
    >
      <span style={{ color: "#73879C" }}>{label}</span>
      <span
        style={{
          color: "#1f2933",
          fontFamily: mono ? "monospace" : undefined,
          fontSize: mono ? ".78rem" : undefined,
          display: "flex",
          alignItems: "center",
          gap: "0.4rem",
        }}
      >
        {swatchColor && (
          <span
            aria-hidden
            style={{
              display: "inline-block",
              width: 14,
              height: 14,
              borderRadius: 3,
              background: swatchColor,
              border: "1px solid #dee2e6",
            }}
          />
        )}
        {rendered === null ? (
          <span style={{ color: "#adb5bd" }}>—</span>
        ) : link && rendered ? (
          <a
            href={link}
            style={{ color: "#0069d9", textDecoration: "none" }}
            target={link.startsWith("http") ? "_blank" : undefined}
            rel={link.startsWith("http") ? "noreferrer" : undefined}
          >
            {rendered}
          </a>
        ) : (
          rendered
        )}
      </span>
    </div>
  );
}
